import React, { useState, useEffect, useMemo } from 'react';
import api, { asList } from '../utils/api';
import API from '../utils/apiconfig';
import { useAuth } from '../context/Authcontext';
import { useGlobalError } from '../context/GlobalErrorContext';
import { ScrollableListBody } from '../components/ScrollableTable';
import TablePagination from '../components/TablePagination';
import { DEFAULT_PAGE_SIZE, paginateClientList } from '../utils/pagination';
import { getRankDisplay, rankTheme } from '../utils/rankBadge';

const RANK_ICONS  = { bronze: '🥉', silver: '🥈', gold: '🥇', platinum: '💎', diamond: '✨' };
const RANK_COLORS = {
  bronze:   'bg-orange-50 text-orange-600 border-orange-200',
  silver:   'bg-slate-50 text-slate-500 border-slate-300',
  gold:     'bg-amber-50 text-amber-600 border-amber-200',
  platinum: 'bg-cyan-50 text-cyan-600 border-cyan-200',
  diamond:  'bg-violet-50 text-violet-600 border-violet-200',
};

function Leaderboard() {
  const [leaders, setLeaders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const { user }              = useAuth();
  const { reportError } = useGlobalError();
  const isAdminViewer = Boolean(
    user?.is_staff || user?.is_superuser || user?.role === 'admin'
  );

  useEffect(() => {
    api.get(API.GET_LEADERBOARD)
      .then(r => { setLeaders(asList(r.data)); setLoading(false); })
      .catch((err) => { setLoading(false); reportError(err); });
  }, [reportError]);

  const pagedLeaders = useMemo(
    () => paginateClientList(leaders, page, pageSize),
    [leaders, page, pageSize]
  );

  // Points are admin-only: backend omits them for members; UI must not show either.
  const canSeePoints = useMemo(() => {
    if (!isAdminViewer) return false;
    if (leaders.length === 0) return true;
    return leaders.some(
      (l) => l.total_earned_points != null || l.reward_points != null
    );
  }, [isAdminViewer, leaders]);

  return (
    <div className="min-h-screen bg-slate-50 py-10">
      <div className="max-w-xl mx-auto px-6">

        <div className="text-center mb-10">
          <p className="text-xs font-bold text-slate-400 uppercase tracking-widest mb-3">Community Rankings</p>
          <h1 className="text-3xl font-black text-slate-900 tracking-tight mb-2"
            style={{ fontFamily: "'DM Serif Display', Georgia, serif" }}>Top Earners</h1>
          <p className="text-sm text-slate-400">
            {canSeePoints
              ? 'Based on lifetime points earned'
              : 'Ranked by community activity — point totals are hidden for members'}
          </p>
          {canSeePoints ? (
            <p className="mt-2 inline-flex rounded-full border border-violet-200 bg-violet-50 px-2.5 py-1 text-[10px] font-bold uppercase tracking-wide text-violet-800">
              Admin view — points visible
            </p>
          ) : null}
        </div>

        {/* Podium */}
        {!loading && leaders.length >= 3 && (
          <div className="bg-white border border-slate-200 rounded-2xl p-6 mb-4 flex items-end justify-center gap-3">
            {/* 2nd */}
            <div className="flex flex-col items-center flex-1">
              <div className="w-10 h-10 rounded-xl bg-slate-100 flex items-center justify-center text-sm font-black text-slate-600 mb-2">
                {leaders[1]?.username?.[0]?.toUpperCase()}
              </div>
              <p className="text-xs font-semibold text-slate-700 mb-0.5">{leaders[1]?.username}</p>
              {canSeePoints && (
                <p className="text-xs text-slate-400">⭐ {leaders[1]?.total_earned_points}</p>
              )}
              <div className="mt-3 w-full h-16 bg-slate-50 border-t border-slate-100 rounded-t-xl flex items-start justify-center pt-2">
                <span className="text-xl">🥈</span>
              </div>
            </div>
            {/* 1st */}
            <div className="flex flex-col items-center flex-1 mb-5">
              <span className="text-lg mb-1">👑</span>
              <div className="w-12 h-12 rounded-xl bg-slate-900 flex items-center justify-center text-base font-black text-white mb-2">
                {leaders[0]?.username?.[0]?.toUpperCase()}
              </div>
              <p className="text-sm font-black text-slate-900 mb-0.5" style={{ fontFamily: "'DM Serif Display', Georgia, serif" }}>{leaders[0]?.username}</p>
              {canSeePoints && (
                <p className="text-xs text-amber-600 font-bold">⭐ {leaders[0]?.total_earned_points}</p>
              )}
              <div className="mt-3 w-full h-24 bg-amber-50 border-t border-amber-100 rounded-t-xl flex items-start justify-center pt-2">
                <span className="text-xl">🥇</span>
              </div>
            </div>
            {/* 3rd */}
            <div className="flex flex-col items-center flex-1">
              <div className="w-10 h-10 rounded-xl bg-slate-50 border border-slate-100 flex items-center justify-center text-sm font-black text-slate-500 mb-2">
                {leaders[2]?.username?.[0]?.toUpperCase()}
              </div>
              <p className="text-xs font-semibold text-slate-700 mb-0.5">{leaders[2]?.username}</p>
              {canSeePoints && (
                <p className="text-xs text-slate-400">⭐ {leaders[2]?.total_earned_points}</p>
              )}
              <div className="mt-3 w-full h-10 bg-slate-50 border-t border-slate-100 rounded-t-xl flex items-start justify-center pt-1">
                <span className="text-xl">🥉</span>
              </div>
            </div>
          </div>
        )}

        {/* Full list */}
        <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden mb-5">
          <div className="px-5 py-3.5 border-b border-slate-100">
            <p className="text-xs font-bold text-slate-400 uppercase tracking-widest">All Rankings</p>
          </div>
          {loading && leaders.length === 0 ? null : (
            <>
            <ScrollableListBody>
              {pagedLeaders.items.map((leader, i) => {
                const globalIndex = (pagedLeaders.page - 1) * pageSize + i;
                const isMe    = user?.username === leader.username;
                const medals  = ['🥇', '🥈', '🥉'];
                const rankBadge = getRankDisplay(leader);
                const { colorClass: rColor, icon: rIcon } = rankTheme(
                  rankBadge.themeKey,
                  RANK_COLORS,
                  RANK_ICONS
                );
                return (
                  <div key={leader.id} className={`flex items-center gap-3 px-5 py-3.5 ${isMe ? 'bg-amber-50/50' : ''}`}>
                    <span className="text-base min-w-[28px] text-center">
                      {globalIndex < 3 ? medals[globalIndex] : <span className="text-xs font-bold text-slate-400">#{globalIndex + 1}</span>}
                    </span>
                    <div className={`w-8 h-8 rounded-xl flex items-center justify-center text-xs font-black flex-shrink-0 ${
                      isMe ? 'bg-slate-900 text-white' : 'bg-slate-100 text-slate-500'
                    }`}>
                      {leader.username[0].toUpperCase()}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-slate-800 flex items-center gap-1.5 flex-wrap">
                        {leader.username}
                        {isMe && <span className="text-[10px] bg-slate-900 text-white px-1.5 py-0.5 rounded font-bold">you</span>}
                        {rankBadge.show && (
                          <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full border capitalize ${rColor}`}>
                            {rIcon} {rankBadge.label}
                          </span>
                        )}
                      </p>
                      <p className="text-xs text-slate-400">{leader.referral_count} referred · {leader.membership_status}</p>
                    </div>
                    {canSeePoints && (
                      <>
                        <div className="text-right">
                          <p className="text-sm font-black text-slate-900">⭐ {(leader.total_earned_points || 0).toLocaleString('en-IN')}</p>
                          <p className="text-xs text-slate-300">lifetime pts</p>
                        </div>
                        <div className="w-16 h-1.5 bg-slate-100 rounded-full overflow-hidden ml-1">
                          <div className="h-full bg-slate-900 rounded-full"
                            style={{ width: `${leaders[0]?.total_earned_points > 0 ? (leader.total_earned_points / leaders[0].total_earned_points) * 100 : 0}%` }} />
                        </div>
                      </>
                    )}
                  </div>
                );
              })}
            </ScrollableListBody>
            <TablePagination
              page={pagedLeaders.page}
              totalPages={pagedLeaders.totalPages}
              onPageChange={setPage}
              pageSize={pageSize}
              onPageSizeChange={(next) => { setPageSize(next); setPage(1); }}
              disabled={loading}
              count={pagedLeaders.count}
              rowsPerPageId="leaderboard-rows-per-page"
              embedded
              className="px-4"
            />
            </>
          )}
        </div>

        {user && (
          <div className="bg-white border border-slate-200 rounded-2xl p-6 text-center">
            <p className="text-sm font-bold text-slate-800 mb-1">Want to climb the leaderboard?</p>
            <p className="text-xs text-slate-400 mb-4">
              {canSeePoints
                ? 'Share your referral code and earn points every month your network stays active.'
                : 'Share your referral code and grow your active network to move up the rankings.'}
            </p>
            <a href="/network" className="inline-block text-sm font-semibold bg-slate-900 hover:bg-slate-700 text-white px-5 py-2.5 rounded-xl transition-colors no-underline">
              🔗 Share My Referral Link
            </a>
          </div>
        )}
      </div>
    </div>
  );
}

export default Leaderboard;